'use client';

import { usePathname } from 'next/navigation';
import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback
} from 'react';
import { ALL_AD_SLOTS as allAdSlots } from '@/constants/ads.constants';

type AdsContextType = {
  adSlots: Record<string, googletag.Slot>;
  defineSlot: (id: string) => void;
  destroySlot: (id: string) => void;
  refreshAds: (slotIds?: string[]) => void;
  setTargeting: (targetingObj: {
    [key in AdManagerTargetingKeys]?: string | null;
  }) => void;
};

type googletag = typeof window.googletag;

export const AdsContext = createContext<AdsContextType | undefined>(undefined);

const adSlots: Record<string, googletag.Slot> = {};

export default function AdsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [refreshCount, setRefreshCount] = useState(0);
  const pathname = usePathname();
  const [googletag, setGoogletag] = useState<googletag | null>(null);

  const refreshAds = useCallback(
    (slotIds?: string[]) => {
      if (!googletag) return;

      googletag.cmd.push(() => {
        const slots = slotIds
          ? [...slotIds.map((id) => adSlots[id])]
          : undefined;

        googletag.pubads().refresh(slots);
      });
    },
    [googletag]
  );

  const defineSlot = useCallback(
    (id: string) => {
      if (!googletag) return;

      const slot = allAdSlots[id];
      const { size, sizeMapping } = slot;

      googletag.cmd.push(() => {
        if (adSlots[id]) return;

        const gptSlot = googletag.defineSlot(`/34178149/${id}`, size, id);
        if (gptSlot) {
          gptSlot.addService(googletag.pubads());

          const mapping = googletag.sizeMapping();
          if (sizeMapping?.desktop) {
            mapping.addSize([1024, 768], sizeMapping.desktop);
          }
          if (sizeMapping?.mobile) {
            mapping.addSize([300, 0], sizeMapping.mobile);
          }
          const builtMapping = mapping.build();
          if (builtMapping) {
            gptSlot.defineSizeMapping(builtMapping);
          }

          adSlots[id] = gptSlot;
        }
      });
    },
    [googletag]
  );

  const destroySlot = useCallback(
    (id: string) => {
      if (!googletag) return;
      googletag.cmd.push(() => {
        const slot = adSlots[id];
        if (slot) {
          googletag.destroySlots([slot]);
          delete adSlots[id];
        }
      });
    },
    [googletag]
  );

  const setTargeting = useCallback(
    (targetingObj: {
      [key in AdManagerTargetingKeys]?: string | null;
    }) => {
      if (!googletag) return;
      googletag.cmd.push(() => {
        googletag.setConfig({
          targeting: targetingObj
        });
      });
    },
    [googletag]
  );

  useEffect(() => {
    const gptObject = window.googletag;

    setGoogletag(gptObject);
  }, []);

  useEffect(() => {
    if (!googletag) return;
    googletag.cmd.push(() => {
      // Disable initial load, to precisely control when ads are requested.
      // Enable SRA and services.
      googletag.setConfig({
        disableInitialLoad: true,
        singleRequest: true,
        collapseDiv: 'ON_NO_FILL',
        lazyLoad: {
          fetchMarginPercent: 200, // Fetch slots within 2 viewports.
          renderMarginPercent: 50, // Render slots within 0.5 viewports.
          mobileScaling: 2.0 // Double for mobile.
        }
      });

      googletag.enableServices();
    });
  }, [googletag]);

  useEffect(() => {
    setTargeting({ pathname: pathname });
    refreshAds();
    setRefreshCount(0); // Reset refresh count on route change.
  }, [pathname, refreshAds, setTargeting]);

  useEffect(() => {
    if (refreshCount >= 5) return; // Stop after 5 refreshes.

    const interval = setInterval(() => {
      refreshAds();
      setRefreshCount(refreshCount + 1);
    }, 30000); // Refresh every 30 seconds.

    return () => clearInterval(interval);
  }, [refreshAds, refreshCount]);

  return (
    <AdsContext.Provider
      value={{
        adSlots,
        defineSlot,
        destroySlot,
        refreshAds,
        setTargeting
      }}
    >
      {children}
    </AdsContext.Provider>
  );
}

export function useAds() {
  const context = useContext(AdsContext);
  if (context === undefined) {
    throw new Error('useAds must be used within an AdsProvider');
  }

  return context;
}
